<template>
  <div
    v-if="showWarning"
    :class="`regulatory-update-warning ${selectedWarning.class}`"
    data-testid="regulatory-update-warning"
  >
    <BaseText :color="selectedWarning.color" :size="textSize">
      {{ selectedWarning.text }}
    </BaseText>

    <Button
      :colors="selectedWarning.button?.color"
      @click="() => selectedWarning.button?.action()"
    >
      <div class="regulatory-update-warning__button">
        <BaseText :color="selectedWarning.color" :size="textSize">
          {{ selectedWarning.button?.label }}
        </BaseText>
        <BaseIcon
          :icon="selectedWarning.icon"
          :height="iconSize"
          :width="iconSize"
        />
      </div>
    </Button>
  </div>
</template>

<script lang="ts">
import Bowser from 'bowser';
import { namespace } from 'vuex-class';
import { Component, Prop, Vue } from 'vue-property-decorator';
import { NebraskaColors } from '@warrenbrasil/nebraska-tokens-web';
import {
  BaseText,
  ButtonFoundation as Button,
  BaseIcon
} from '@warrenbrasil/nebraska-web';

import {
  getRegulatoryUpdateStatus,
  RegulatoryUpdateStatus,
  RegulatoryUpdateStatusReason
} from '@/modules/common/services/http/account';
import { WarrenModules } from '@/types/navigation';
import { trackGoToRegulatoryUpdate } from './track-events';

const customerModule = namespace('CustomerModule');

@Component({ components: { BaseText, Button, BaseIcon } })
export default class RegulatoryUpdateWarning extends Vue {
  @Prop({ type: String, default: WarrenModules.Home })
  private cameFrom!: string;

  private status = RegulatoryUpdateStatus.UP_TO_DATE;
  private statusReason = RegulatoryUpdateStatusReason.NONE;
  private activeWarning = true;

  readonly NebraskaColors = NebraskaColors;

  @customerModule.Getter('getCustomerId')
  private customerId!: string;

  @customerModule.Mutation('setRegulatoryUpdateStatus')
  private setRegulatoryUpdateStatus!: (value: RegulatoryUpdateStatus) => void;

  get warningStatusReason() {
    return {
      none: {
        color: NebraskaColors.statusAlert,
        class: 'regulatory-update-warning__none',
        text: this.statusText,
        button: {
          label: 'Atualizar cadastro',
          action: () => this.goToRegulatoryUpdate(),
          color: {
            background: NebraskaColors.statusAlertBackground,
            content: NebraskaColors.statusAlert,
            alphas: {
              default: 1,
              hover: 0.9,
              active: 0.8
            }
          }
        },
        icon: 'EA0170'
      },
      in_analysis: {
        color: NebraskaColors.statusInfo,
        class: 'regulatory-update-warning__analysis',
        text: 'Seu cadastro está em análise',
        button: {
          label: null,
          action: () => this.closeWarning(),
          color: {
            background: NebraskaColors.statusInfoBackground,
            content: NebraskaColors.statusInfo,
            alphas: {
              default: 1,
              hover: 0.9,
              active: 0.8
            }
          }
        },
        icon: 'EF0030'
      },
      pending_residence_proof: {
        color: NebraskaColors.statusAlert,
        class: 'regulatory-update-warning__none',
        text: 'Novas pendências',
        button: {
          label: 'Atualizar cadastro',
          action: () => this.goToRegulatoryUpdate(),
          color: {
            background: NebraskaColors.statusAlertBackground,
            content: NebraskaColors.statusAlert,
            alphas: {
              default: 1,
              hover: 0.9,
              active: 0.8
            }
          }
        },
        icon: 'EA0170'
      }
    };
  }

  get selectedWarning() {
    return this.warningStatusReason[this.statusReason];
  }

  mounted() {
    this.getStatus();
  }

  private async getStatus() {
    try {
      const { status, statusReason } = await getRegulatoryUpdateStatus(
        this.customerId
      );
      this.status = status;
      this.statusReason = statusReason || RegulatoryUpdateStatusReason.NONE;
      this.setRegulatoryUpdateStatus(this.status);
    } catch (e) {
      this.status = RegulatoryUpdateStatus.UP_TO_DATE;
      this.setRegulatoryUpdateStatus(this.status);
    }
  }

  private get isMobile(): boolean {
    const browser = Bowser.getParser(window.navigator.userAgent);
    return browser.getPlatformType() !== 'desktop';
  }

  public get textSize(): string {
    return this.isMobile ? 'xs' : 'md';
  }

  public get iconSize(): string {
    return this.isMobile ? '16' : '24';
  }

  private closeWarning() {
    this.activeWarning = false;
  }

  public get showWarning(): boolean {
    return (
      this.activeWarning && this.status !== RegulatoryUpdateStatus.UP_TO_DATE
    );
  }

  public get statusText(): string {
    if (this.status === RegulatoryUpdateStatus.BLOCKED) {
      return 'Conta bloqueada para investir';
    }

    return 'Atualização cadastral pendente';
  }

  public goToRegulatoryUpdate() {
    trackGoToRegulatoryUpdate();
    this.$router.push({
      name: 'regulatoryUpdateAnnouncement'
    });
  }
}
</script>

<style lang="less" scoped>
@navbar-height: 64px;

.regulatory-update-warning {
  width: 100%;
  position: fixed;
  top: 0;
  left: 0;

  display: flex;
  justify-content: space-between;
  align-items: center;

  background-color: @status-alert-background;
  padding: @size-spacing-x450 @size-spacing-x600;
  margin-top: @navbar-height;
  z-index: 1;

  &__analysis {
    background-color: @status-info-background;
  }

  @media @tabletDown {
    @navbar-height: 56px;
    padding: @size-spacing-x450 @size-spacing-x450;
    margin-top: @navbar-height;
  }

  &__button {
    display: flex;
    align-items: center;
    gap: @size-spacing-x500;

    @media @tabletDown {
      gap: 0;
    }

    & p {
      font-weight: bold;
    }
  }
}
</style>
